import { useLazyQuery, useMutation } from "@apollo/client";
import { createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import Autocomplete from "components/Autocomplete";
import Dialog from "components/Dialog";
import GeoAutoCompletion from "components/GeoAutoCompletion";
import { TextField } from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import useText from "constants/text";
import { GET_RECEIVER_BY_ID, UPDATE_ORDER } from "domains/Task/graphql/mutations";
import { Hub, Order, Receiver } from "generated/graphql";
import { useCountryCode } from "hooks/useCurrentUser";
import React, { useMemo, useState } from "react";
import "styled-components/macro";
import { ValidReceiver } from "utils";

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    updateReceiver: {
      padding: "54px 54px",
      [theme.breakpoints.down("xs")]: { padding: "6px" },
    },
  })
);
export interface ReceiverProps {
  open: boolean;
  handleClose: () => void;
  refetch?: () => void;
  handlerUpdate: any;
  order: Order;
  clientId?: string;
}

export function UpdateReceiver({ open, handleClose, handlerUpdate, refetch, order }: ReceiverProps) {
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const [data, setData] = React.useState(order);
  const { text } = useText();
  const [formValues, setFormValues] = React.useState(order?.order_receiver);

  const [relayValues, setRelayValues] = useState(order?.relay);
  const [cityValue, setCityValue] = useState<string>(null);
  const [inputValueRelayPoint, setInputValueRelayPoint] = useState<Hub>(null);
  const [relays] = useState([]);
  const [relayPointCity] = useState([]);
  const countryCode = useCountryCode();
  const [updateOrder] = useMutation(UPDATE_ORDER);
  const [receiverById, { data: receiverData }] = useLazyQuery(GET_RECEIVER_BY_ID);
  const [changeReceiver, setChangeReceiver] = React.useState<Receiver>();

  const handleInputChange = React.useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  React.useEffect(() => {
    if (order && order?.order_receiver) {
      setFormValues(order.order_receiver);
      setRelayValues(order.relay);
      setData(order);
    }
  }, [order]);

  React.useEffect(() => {
    if (formValues && formValues !== null) {
      setData((prev) => ({
        ...prev,
        order_receiver: {
          ...formValues,
        },
        relay: {
          ...relayValues,
        },
      }));
    }
  }, [formValues, relayValues]);
  React.useEffect(() => {
    const dataToSend = receiverData?.results?.data;
    if (dataToSend) {
      const {
        receiver_email,
        receiver_entreprise,
        receiver_firstname,
        receiver_lastname,
        receiver_phone_number,
        receiver_specification_address,
        receiver_address,
      } = dataToSend;

      setChangeReceiver({
        receiver_email,
        receiver_entreprise,
        receiver_firstname,
        receiver_lastname,
        receiver_phone_number,
        receiver_specification_address,
        receiver_address,
      });
    } else {
      setChangeReceiver(formValues);
    }
  }, [receiverData, formValues]);

  const handleConfirm = () => {
    setFormSubmited(true);
    const phone = formValues.receiver_phone_number?.replace(/(?!\+)\D/g, "");

    if (!isValidPhoneNumberFormat(formValues.receiver_phone_number)) return;
    if (
      formValues?.receiver_address?.place_id === undefined ||
      formValues?.receiver_address?.place_id === null ||
      formValues?.receiver_address?.place_id?.length === 0
    )
      return;

    const keys = Object.keys(formValues);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key !== "receiver_entreprise" && formValues[key]?.length === 0) return;
    }

    if (formValues.receiver_address === "" || formValues.receiver_address === undefined) return;
    if (
      formValues.receiver_phone_number === "" ||
      formValues.receiver_phone_number === undefined ||
      formValues.receiver_phone_number === null ||
      formValues?.receiver_address?.address === undefined ||
      formValues?.receiver_address?.address === null ||
      formValues?.receiver_address?.address?.length === 0
    )
      return;
    handlerUpdate({
      ...data,
      order_receiver: {
        ...formValues,
        receiver_phone_number: phone,
      },
      relay: {
        ...relayValues,
        name: inputValueRelayPoint?.name,
        location: {
          city: cityValue,
        },
      },
    });
    refetch();
    setFormSubmited(false);
    handleClose();
  };
  const onChange = (event: any) => {
    event?.persist();
    setFormValues((prev) => ({
      ...prev,
      [event?.target?.name]: event?.target?.value,
    }));
  };
  const isValidPhone = () => isValidPhoneNumberFormat(formValues.receiver_phone_number);

  const handleSelect = (event: any, name: string) => {
    setCityValue(name);
    setRelayValues({ ...relayValues, [event.target.Location.city]: name });
  };

  const handleOnInputRelaypointChange = (event: any, newInputValue: Hub) => {
    setInputValueRelayPoint({
      ...inputValueRelayPoint,
      [event.target.name]: newInputValue,
    });
    setRelayValues({ ...relayValues, [event.target.name]: newInputValue });
  };

  const getOption = () => {
    (option: any) => option?.name;
  };
  const styles = useStyles();

  const isFormValid = useMemo(() => ValidReceiver.isValidSync(formValues), [formValues]);
  return (
    <Dialog
      title={text.all.update_receiver}
      open={open}
      confirmButtonTitle={text.all.update}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      disableConfirmButton={!isFormValid}
      maxWidth="md"
      key="add receiver dialog"
    >
      <Grid container spacing={4} className={styles.updateReceiver}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={text.all.first_name}
            name="receiver_firstname"
            value={formValues?.receiver_firstname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.receiver_firstname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_firstname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={text.all.last_name}
            name="receiver_lastname"
            value={formValues?.receiver_lastname}
            onChange={onChange}
            error={isFormSubmited && formValues?.receiver_lastname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_lastname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={text.all.entreprise}
            name="receiver_entreprise"
            value={formValues?.receiver_entreprise}
            onChange={onChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PhoneField
            countryCode={countryCode}
            fullWidth
            label={text.all.phone_number}
            name="receiver_phone_number"
            value={formValues?.receiver_phone_number}
            onValueChange={(value) => {
              setFormValues({
                ...formValues,
                receiver_phone_number: value,
              });
            }}
            error={isFormSubmited && !isValidPhone()}
            helperText={isFormSubmited && !isValidPhone() ? `${text.all.this_field_is_required}` : ""}
            required
            type="tel"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <GeoAutoCompletion
            onChange={(values) => setFormValues((prev) => ({ ...prev, receiver_address: values }))}
            address={formValues?.receiver_address}
            error={
              (isFormSubmited && formValues?.receiver_address?.place_id === undefined) ||
              formValues?.receiver_address?.place_id === null ||
              formValues?.receiver_address?.address?.length === 0 ||
              formValues?.receiver_address?.address?.length === undefined
            }
            helperText={
              (isFormSubmited && formValues?.receiver_address?.place_id === undefined) ||
              formValues?.receiver_address?.place_id === null ||
              formValues?.receiver_address?.address?.length === 0 ||
              formValues?.receiver_address?.address?.length === undefined
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={text.all.specification}
            name="receiver_specification_address"
            value={formValues?.receiver_specification_address}
            helperText={
              isFormSubmited && formValues?.receiver_specification_address?.length === 0
                ? `${text.all.this_field_is_required}`
                : ""
            }
            error={isFormSubmited && formValues?.receiver_specification_address?.length === 0}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={relayPointCity}
            value={relayValues?.location?.city}
            onChange={handleSelect}
            label={text.all.city}
            name="city"
            getOptionLabel={getOption}
            renderInput={(params: any) => <TextField {...params} variant="outlined" />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={relays}
            value={relayValues?.name}
            onChange={handleOnInputRelaypointChange}
            label={text.all.relay_point}
            name="name"
            getOptionLabel={getOption}
            renderInput={(params: any) => <TextField {...params} variant="outlined" />}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
}

export default UpdateReceiver;
